import { useEffect, useRef } from "react";
import { Head, router, useForm } from "@inertiajs/react";
import Tooltip from "bootstrap/js/dist/tooltip";
import AuthLayout from "@/layouts/auth-layout";
import {
  download_file_manage_chatbot_prompt_instruction_path,
  file_manage_chatbot_prompt_instruction_path,
  files_manage_chatbot_prompt_instruction_path,
  manage_chatbot_prompt_instruction_path,
  manage_products_path
} from "@/routes";

function buildPromptInstructionFormData(promptInstruction) {
  return {
    prompt_instruction: {
      instructions: promptInstruction?.instructions || ""
    }
  };
}

function formatBytes(value) {
  if (!value) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const size = value / (1024 ** index);

  return `${new Intl.NumberFormat("da-DK", { maximumFractionDigits: index === 0 ? 0 : 1 }).format(size)} ${units[index]}`;
}

function fileTypeLabel(file) {
  const filenameExtension = file.filename?.split(".").pop()?.toUpperCase();
  if (filenameExtension && filenameExtension !== file.filename?.toUpperCase()) return filenameExtension;

  return "Fil";
}

function OpenaiStatusIcon({ file }) {
  if (file.openai_status === "failed") {
    return (
      <i
        className="bi bi-exclamation-triangle-fill text-danger fs-5"
        data-bs-toggle="tooltip"
        data-bs-placement="left"
        title="Der opstod en fejl under synkroniseringen. Slet filen og prøv igen."
        aria-label="Der opstod en fejl under synkroniseringen. Slet filen og prøv igen."
      />
    );
  }

  if (file.openai_status === "synced") {
    return (
      <i
        className="bi bi-check-circle-fill text-success fs-5"
        data-bs-toggle="tooltip"
        data-bs-placement="left"
        title="Synkroniseret til OpenAI."
        aria-label="Synkroniseret til OpenAI."
      />
    );
  }

  return (
    <span
      className="spinner-border spinner-border-sm text-secondary"
      data-bs-toggle="tooltip"
      data-bs-placement="left"
      role="status"
      title="Synkronisering igang."
      aria-label="Synkronisering igang."
    />
  );
}

function ChatbotPromptInstructionsShow({ prompt_instruction: promptInstruction, errors = [] }) {
  const { data, setData, put, delete: destroy, processing } = useForm(buildPromptInstructionFormData(promptInstruction));
  const uploadForm = useForm({ prompt_instruction_file: { files: [] } });
  const fileInputRef = useRef(null);
  const filesTableRef = useRef(null);
  const files = promptInstruction?.files || [];
  const hasPendingFiles = files.some((file) => file.openai_status === "pending");

  useEffect(() => {
    setData(buildPromptInstructionFormData(promptInstruction));
  }, [promptInstruction, setData]);

  useEffect(() => {
    if (!hasPendingFiles) return undefined;

    const timer = window.setInterval(() => {
      router.reload({
        only: ["prompt_instruction"],
        preserveScroll: true
      });
    }, 3000);

    return () => window.clearInterval(timer);
  }, [hasPendingFiles]);

  useEffect(() => {
    if (!filesTableRef.current) return undefined;

    const tooltips = Array.from(filesTableRef.current.querySelectorAll('[data-bs-toggle="tooltip"]')).map((element) => new Tooltip(element));

    return () => tooltips.forEach((tooltip) => tooltip.dispose());
  }, [files]);

  const updatePromptInstruction = (nextAttributes) => {
    setData("prompt_instruction", {
      ...data.prompt_instruction,
      ...nextAttributes
    });
  };

  const saveInstructions = (event) => {
    event.preventDefault();
    put(manage_chatbot_prompt_instruction_path(), { preserveScroll: true });
  };

  const uploadFile = (event) => {
    event.preventDefault();
    uploadForm.post(files_manage_chatbot_prompt_instruction_path(), {
      preserveScroll: true,
      forceFormData: true,
      onSuccess: () => {
        uploadForm.reset();
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    });
  };

  const removeFile = (file) => {
    destroy(file_manage_chatbot_prompt_instruction_path(file.id), { preserveScroll: true });
  };

  return (
    <>
      <Head title="Chat instruktioner" />

      <section className="workspace-content organization-files-page">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="workspace-section__eyebrow">Chat</div>
                <h2 className="workspace-card-title">Generelle instruktioner</h2>
                <p className="workspace-card-description">
                  Disse instruktioner og filer bruges internt af chatbotten. Produktviden til kunder administreres på produkterne.
                </p>

                {errors.length > 0 ? (
                  <div className="alert alert-danger">
                    {errors.join(", ")}
                  </div>
                ) : null}

                <form className="vstack gap-4" onSubmit={saveInstructions}>
                  <div>
                    <textarea
                      id="prompt-instruction-instructions"
                      className="form-control font-monospace"
                      rows="18"
                      value={data.prompt_instruction.instructions}
                      onChange={(event) => updatePromptInstruction({ instructions: event.target.value })}
                      required
                    />
                  </div>

                  <div className="d-flex flex-wrap justify-content-end gap-3">
                    <button className="btn btn-primary px-4" disabled={processing}>
                      Gem instruktioner
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="card mt-4">
              <div className="card-body">
                <div className="workspace-section__eyebrow">Chatbot-filer</div>
                <h2 className="workspace-section__title">Private instruktioner</h2>
                <p className="workspace-admin-copy mb-0">
                  Upload interne filer, som kun bruges til at instruere chatbotten. Disse filer vises ikke for kunderne.
                </p>
                <form className="row g-3 align-items-end mt-2" onSubmit={uploadFile}>
                  <div className="col-12 col-lg-8">
                    <label className="form-label fw-semibold" htmlFor="prompt-instruction-files">
                      Tilføj filer
                    </label>
                    <input
                      id="prompt-instruction-files"
                      ref={fileInputRef}
                      className="form-control"
                      type="file"
                      multiple
                      onChange={(event) => uploadForm.setData("prompt_instruction_file", { files: Array.from(event.target.files || []) })}
                      required
                    />
                  </div>
                  <div className="col-12 col-lg-4">
                    <button className="btn btn-primary w-100" disabled={uploadForm.processing || uploadForm.data.prompt_instruction_file.files.length === 0}>
                      Upload filer
                    </button>
                  </div>
                </form>

                <div className="workspace-admin-table mt-4" ref={filesTableRef}>
                  <div className="table-responsive">
                    <table className="table align-middle mb-0 organization-files-table">
                      <colgroup>
                        <col className="organization-files-table__name-column" />
                        <col className="organization-files-table__type-column" />
                        <col className="organization-files-table__size-column" />
                        <col className="organization-files-table__status-column" />
                        <col className="organization-files-table__actions-column organization-files-table__actions-column--double" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Fil</th>
                          <th>Type</th>
                          <th>Størrelse</th>
                          <th>AI-status</th>
                          <th className="text-end">Handling</th>
                        </tr>
                      </thead>
                      <tbody>
                        {files.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-muted py-4">
                              Der er ingen private chatbot-filer endnu.
                            </td>
                          </tr>
                        ) : (
                          files.map((file) => (
                            <tr key={file.id}>
                              <td>
                                <div className="organization-files-table__file">
                                  <span className="organization-files-table__icon">
                                    <i className="bi bi-file-earmark" aria-hidden="true" />
                                  </span>
                                  <span className="organization-files-table__name" title={file.filename || file.name}>{file.filename || file.name}</span>
                                </div>
                              </td>
                              <td>
                                <span className="workspace-chip">{fileTypeLabel(file)}</span>
                              </td>
                              <td className="text-muted">{formatBytes(file.byte_size)}</td>
                              <td>
                                <OpenaiStatusIcon file={file} />
                              </td>
                              <td className="text-end">
                                <div className="organization-files-table__actions">
                                  <a
                                    href={download_file_manage_chatbot_prompt_instruction_path(file.id)}
                                    className="btn btn-outline-secondary organization-files-table__action"
                                    aria-label={`Download ${file.filename || file.name}`}
                                    title="Download"
                                  >
                                    <i className="bi bi-download" aria-hidden="true" />
                                  </a>

                                  <button
                                    type="button"
                                    className="btn btn-outline-danger organization-files-table__action"
                                    aria-label={`Fjern ${file.filename || file.name}`}
                                    title="Fjern"
                                    onClick={() => removeFile(file)}
                                    disabled={processing}
                                  >
                                    <i className="bi bi-trash" aria-hidden="true" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}

ChatbotPromptInstructionsShow.layout = (page) => (
  <AuthLayout
    header={{
      breadcrumbs: [
        { label: "Administration", href: manage_products_path() },
        { label: "Chat instruktioner" }
      ],
      title: "Chat instruktioner"
    }}
  >
    {page}
  </AuthLayout>
);

export default ChatbotPromptInstructionsShow;
